import { useEffect, useState, useRef } from "react"
import CarouselItem from "./carousel-item"
import styled, { keyframes, css } from "styled-components"

const shadowTheme = css`
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.12);
  border-radius: 16px;
  padding: 32px 64px;
  max-width: initial;
  position: relative;

  blockquote {
    margin: 0;
  }
`

const darkTheme = css`
  background: var(--color-black);
  color: white;
  border-radius: 16px;
  padding: 0 32px 32px;
  @media (min-width: 544px) {
    padding: 0 128px 32px;
  }
  max-width: initial;
  position: relative;
  text-align: center;
  margin-top: 16px;
  max-width: 940px;

  .carouselLogo {
    display: flex;
    align-items: center;
    padding: 32px;
    background: var(--grey-20);
    border-radius: 8px;
    position: relative;
    top: -18px;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 40px;
    max-width: 160px;
    max-height: 80px;

    img {
      width: 100%;
      height: 100%;
      max-height: 48px;
    }
  }

  blockquote {
    font: var(--text-lg);
    line-height: 1.4;
    margin: 0 0 32px;
  }

  .name {
    font: var(--text-sm);
    font-weight: bold;
    color: var(--grey-40);
  }

  .title {
    font: var(--text-sm);
    color: var(--grey-40);
  }

  .cta {
    margin-top: 32px;

    // specificity hack to override arrowRight button
    &.cta button {
      padding-top: 0;
      padding-bottom: 0;
      padding-right: 24px; /* make space for the arrow */
      color: var(--blue-light);
      font: var(--text-md);
      font-weight: 600;

      &:hover {
        text-decoration: underline;
      }

      &::before {
        top: 50%;
        transform: translate(-4px, -50%);
      }
    }
  }
`

const CarouselContainer = styled.div`
  ${(props) => props.theme === "dark" && `position: relative;`}
  max-width: 940px;
  margin: 0 auto;
`

const Scroll = styled.div`
  max-width: 381px;
  margin: 0 auto;
  display: grid;
  grid-template-areas: "content";
  align-items: flex-start;
  ${(props) => (props.theme === "shadow" ? shadowTheme : "")}
  ${(props) => (props.theme === "dark" ? darkTheme : "")}
`

const CarouselNav = styled.nav`
  margin: 48px auto 0;
  display: flex;
  justify-content: center;
  ${(props) => props.theme === "dark" && `margin-top: 24px;`}
`

const rotate = keyframes`
  0% {
    stroke-dashoffset: 43.9822971503;
  }
  100% {
    stroke-dashoffset: 0;
  }
`

const CarouselDot = styled.div`
  border-radius: 50%;
  margin: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => (props.theme === "shadow" || props.theme === "dark" ? shadowDots : stdDots)}
  ${(props) => props.index === props.activeIndex && props.theme !== "dark" && `opacity: 1;`};
  ${(props) => props.index === props.activeIndex && props.theme === "shadow" && `background: var(--red) !important`};

  ${(props) =>
    props.index === props.activeIndex &&
    props.theme === "dark" &&
    css`
      background: var(--red) !important;

      circle {
        stroke: var(--red);
        stroke-linecap: round;
        stroke-dasharray: 43.9822971503; // circumference of circle: 2 * pi * r
        stroke-dashoffset: 43.9822971503;
        animation: ${rotate} ${props.autoScrollInterval}s linear infinite;
      }

      .progressContainer {
        transform: rotate(-90deg);
        width: 16px;
        height: 16px;
        margin: -8px;
      }
    `}
`

const stdDots = `
  height: 12px;
  width: 12px;
  background: var(--white);
  opacity: 0.1;
`

const shadowDots = `
  background: var(--grey-40);
  height: 8px;
  width: 8px;

  &:hover {
    cursor: pointer;
    background: var(--grey-80);
  }
`

const NavButton = styled.button`
  width: 51px;
  height: 51px;
  padding: 0;
  border: none;
  position: absolute;
  top: calc(50% - 53px);
  left: 0;
  background: url("/g-static/icons/carousel-left-icon.svg") no-repeat;
  cursor: pointer;

  &:hover {
    filter: brightness(0);
  }

  @media (max-width: 767px) {
    display: none;
  }
`

const NavButtonNext = styled(NavButton)`
  transform: rotate(180deg);
  right: 0;
  left: initial;
`

const NavButtonBottom = styled.button`
  width: 33px;
  height: 33px;
  padding: 0;
  border: none;
  position: absolute;
  bottom: -16px;
  left: 0;
  background: url("/g-static/icons/slider-arrow.svg") no-repeat;
  cursor: pointer;
  transform: rotate(180deg);

  &:hover {
    filter: brightness(0);
  }
`
const NavButtonBottomNext = styled(NavButtonBottom)`
  transform: rotate(0);
  right: 0;
  left: initial;
`

export default function BlockCarousel(props) {
  const { autoScroll, autoScrollInterval, children, styles, theme } = props
  const [activeIndex, setActiveIndex] = useState(0)
  const rotate = useRef(0)

  useEffect(() => {
    const getStartIndex = () => {
      const lastIndex = children.length - 1
      let startIndex

      if (activeIndex === 0) {
        startIndex = activeIndex + 1
      } else if (activeIndex === lastIndex) {
        startIndex = 0
      } else {
        startIndex = activeIndex + 1
      }

      return startIndex
    }

    if (autoScroll && children.length > 1) {
      rotate.current = setInterval(() => {
        setActiveIndex(getStartIndex())
      }, autoScrollInterval)
    }

    return () => {
      if (rotate.current) {
        clearInterval(rotate.current)
      }
    }
  }, [activeIndex, children, autoScrollInterval, autoScroll])

  const navigate = (e) => {
    const { current } = rotate
    const index = e.currentTarget.getAttribute("data-index")
    setActiveIndex(parseInt(index, 10))
    if (current) {
      clearInterval(current)
    }
  }

  const navigateNext = () => {
    const totalItems = children.length
    setActiveIndex((prevIndex) => (prevIndex === totalItems - 1 ? 0 : prevIndex + 1))
  }

  const navigatePrevious = () => {
    const totalItems = children.length
    setActiveIndex((prevIndex) => (prevIndex === 0 ? totalItems - 1 : prevIndex - 1))
  }

  const renderItems = () => {
    return children.map((child, index) => (
      <CarouselItem activeIndex={activeIndex} index={index} key={index} item={child} styles={styles} />
    ))
  }

  const renderNav = () => {
    if (children.length > 1) {
      return children.map((_, index) => (
        <CarouselDot
          theme={theme}
          index={index}
          activeIndex={activeIndex}
          key={index}
          data-index={index}
          onClick={navigate}
          autoScrollInterval={autoScrollInterval / 1000}
        >
          {theme === "dark" && (
            <svg className="progressContainer" viewBox="0 0 16 16">
              <circle className="progressCircle" cx="8" cy="8" r="7" fill="none" strokeWidth="1"></circle>
            </svg>
          )}
        </CarouselDot>
      ))
    } else {
      return null
    }
  }

  const CarouselSecondaryNav = () => {
    if (children.length > 1) {
      return (
        <nav>
          <NavButton aria-label="previous" onClick={navigatePrevious} />
          <NavButtonNext aria-label="next" onClick={navigateNext} />
        </nav>
      )
    } else {
      return null
    }
  }

  const CarouselSecondaryNavBottom = () => {
    if (children.length > 1) {
      return (
        <nav>
          <NavButtonBottom aria-label="previous" onClick={navigatePrevious} />
          <NavButtonBottomNext aria-label="next" onClick={navigateNext} />
        </nav>
      )
    } else {
      return null
    }
  }

  return (
    <CarouselContainer theme={theme}>
      <Scroll theme={theme}>{renderItems()}</Scroll>
      <CarouselNav theme={theme}>{renderNav()}</CarouselNav>
      {props.theme === "shadow" && <CarouselSecondaryNav />}
      {props.theme === "dark" && <CarouselSecondaryNavBottom />}
    </CarouselContainer>
  )
}
